import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginSuccess, logout as logoutAction, setLoading, setError as setReduxError } from '../redux/slices/userSlice';
import { useAppSelector, useAppDispatch } from '../redux/store';
import authService, { AuthResponse, LoginCredentials, SignupCredentials } from '../services/auth.service';
import organizationService from '../services/organization.service';
import { logoutThunk, RESET_STATE } from '../redux/thunks/UserThunks';
import {
  clearOrganization,
  setCurrentOrganization as setReduxOrganization,
  setDiscoveredOrganizations,
  setIntermediateSessionToken
} from '../redux/slices/organizationSlice';
import { useOrgNavigate, buildOrgHomeUrl, getOrgSlug } from '../utils/urlHelpers';

interface Organization {
  _id: string;
  name: string;
  slug: string;
  customSlug?: string;
  stytchOrganizationId: string;
}

interface AuthContextType {
  currentUser: AuthResponse | null;
  currentOrganization: Organization | null;
  isAuthenticated: boolean;
  isStytchAuth: boolean;
  isSwitchingOrganization: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithMagicLink: (email: string) => Promise<void>;
  authenticateWithStytch: (sessionToken: string) => Promise<void>;
  createOrganization: (data: {
    organizationName: string;
    organizationSlug: string;
    email: string;
    name: string;
  }) => Promise<void>;
  signup: (credentials: SignupCredentials) => Promise<void>;
  logout: () => void;
  switchOrganization: (organizationId: string, intermediateSessionToken: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Use Redux state instead of local state
  const dispatch = useAppDispatch();
  const userState = useAppSelector((state) => state.UserReducer);
  const [localError, setLocalError] = useState<string | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const navigate = useNavigate();
  const orgNavigate = useOrgNavigate();
  const { i18n, t } = useTranslation();

  // No need for initialization here as it's handled in main.tsx

  const loginWithMagicLink = async (email: string) => {
    try {
      dispatch(setLoading(true));
      setLocalError(null);
      dispatch(setReduxError(null));

      await authService.loginWithMagicLink(email);
      // User will receive email with magic link
      // Navigation handled after token verification
    } catch (err: any) {
      const errorMessage = err.message || t('errors.auth.failedToSendMagicLink', 'Failed to send magic link');
      setLocalError(errorMessage);
      dispatch(setReduxError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const authenticateWithStytch = async (sessionToken: string) => {
    try {
      console.log('🔐 AuthContext: Starting Stytch authentication...');
      dispatch(setLoading(true));
      setLocalError(null);
      dispatch(setReduxError(null));

      console.log('📤 AuthContext: Calling backend authentication endpoint...');
      const response = await authService.authenticateWithStytch(sessionToken);
      console.log('📥 AuthContext: Backend response received:', {
        hasUser: !!response.user,
        hasSessionToken: !!response.sessionToken,
        hasOrganization: !!response.organization,
        userId: response.user?._id
      });

      // Update Redux store
      console.log('💾 AuthContext: Updating Redux store...');
      dispatch(loginSuccess({
        user: response.user,
        accessToken: response.sessionToken
      }));

      // Store organization info
      if (response.organization) {
        console.log('🏢 AuthContext: Setting organization:', response.organization.name);
        setCurrentOrganization(response.organization);
        dispatch(setReduxOrganization(response.organization)); // Sync to Redux
      }

      // Store discovery data for organization switching
      if ((response as any).discoveredOrganizations) {
        console.log('🔍 AuthContext: Storing discovered organizations:', (response as any).discoveredOrganizations.length);
        const discoveredOrgs = (response as any).discoveredOrganizations;
        dispatch(setDiscoveredOrganizations(discoveredOrgs));
        localStorage.setItem('discovered_organizations', JSON.stringify(discoveredOrgs));
      }

      if ((response as any).intermediateSessionToken) {
        console.log('🔑 AuthContext: Storing intermediate session token');
        const ist = (response as any).intermediateSessionToken;
        dispatch(setIntermediateSessionToken(ist));
        localStorage.setItem('intermediate_session_token', ist);
      }

      console.log('🧭 AuthContext: Navigating to home page...');
      // Use organization from response to build URL (don't rely on orgNavigate hook which has stale value)
      const homeUrl = buildOrgHomeUrl(getOrgSlug(response.organization));
      navigate(homeUrl);
      console.log('✅ AuthContext: Authentication flow complete!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || t('errors.auth.stytchAuthFailed', 'Stytch authentication failed');
      console.error('❌ AuthContext: Authentication failed:', errorMessage, err);
      setLocalError(errorMessage);
      dispatch(setReduxError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const createOrganization = async (data: {
    organizationName: string;
    organizationSlug: string;
    email: string;
    name: string;
  }) => {
    try {
      dispatch(setLoading(true));
      setLocalError(null);
      dispatch(setReduxError(null));

      const response = await authService.createOrganization(data);

      // Update Redux store
      dispatch(loginSuccess({
        user: response.user,
        accessToken: response.sessionToken
      }));

      // Store organization info
      if (response.organization) {
        setCurrentOrganization(response.organization);
        dispatch(setReduxOrganization(response.organization)); // Sync to Redux
      }

      orgNavigate('/');
    } catch (err: any) {
      const errorMessage = err.message || t('errors.auth.failedToCreateOrganization', 'Failed to create organization');
      setLocalError(errorMessage);
      dispatch(setReduxError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch(setLoading(true));
      setLocalError(null);
      dispatch(setReduxError(null));

      const response = await authService.login(credentials);

      // Get the user data from the response
      const userData = (response as any).user;

      if (!userData) {
        throw new Error('Invalid login response: missing user data');
      }

      // Dispatch to Redux store
      dispatch(loginSuccess({
        user: userData,
        accessToken: response.accessToken
      }));

      // Store organization if available
      if ((response as any).organization) {
        setCurrentOrganization((response as any).organization);
        dispatch(setReduxOrganization((response as any).organization)); // Sync to Redux
      }

      orgNavigate('/');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        (err.response?.data?.error ? err.response.data.error : t('errors.auth.failedToLogin', 'Failed to login'));
      setLocalError(errorMessage);
      dispatch(setReduxError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const signup = async (credentials: SignupCredentials) => {
    try {
      dispatch(setLoading(true));
      setLocalError(null);
      dispatch(setReduxError(null));

      await authService.signup(credentials);

      // After signup, log the user in
      await login({
        email: credentials.email,
        password: credentials.password
      });
    } catch (err: any) {
      // Extract the error message from the response
      let errorMessage = t('errors.auth.failedToSignUp', 'Failed to sign up');

      if (err.response) {
        if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      }

      setLocalError(errorMessage);
      dispatch(setReduxError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const switchOrganization = async (organizationId: string, intermediateSessionToken: string) => {
    try {
      console.log('🔄 AuthContext: Starting organization switch...', { organizationId });
      setIsSwitchingOrganization(true);
      dispatch(setLoading(true));
      setLocalError(null);
      dispatch(setReduxError(null));

      // CRITICAL: Clear Redux state BEFORE switching to prevent stale data
      console.log('🧹 AuthContext: Clearing Redux state before switch...');
      dispatch({ type: RESET_STATE });
      dispatch(clearOrganization());
      setCurrentOrganization(null);
      // NOTE: Do NOT clear localStorage here - we need the current token to make the API call
      // The backend will return a new token which we'll store after the switch succeeds

      // Retrieve Stytch session token from localStorage if intermediate token is not provided
      let stytchSessionToken: string | undefined;
      if (!intermediateSessionToken) {
        stytchSessionToken = localStorage.getItem('stytch_session_token') || undefined;
      }

      // Call backend to switch organization
      console.log('📤 AuthContext: Calling organization switch endpoint...');
      const response = await organizationService.switchOrganization(
        organizationId,
        intermediateSessionToken,
        stytchSessionToken
      );
      console.log('📥 AuthContext: Organization switch response received:', {
        hasUser: !!response.user,
        hasAccessToken: !!response.accessToken,
        hasOrganization: !!response.organization,
        organizationName: response.organization?.name
      });

      // Update Redux store with new user and organization
      console.log('💾 AuthContext: Updating Redux store with new organization context...');
      dispatch(loginSuccess({
        user: response.user,
        accessToken: response.accessToken
      }));

      // Update organization context
      if (response.organization) {
        setCurrentOrganization(response.organization);
        dispatch(setReduxOrganization(response.organization));
      }

      // Update local storage
      console.log('💾 AuthContext: Updating local storage...');
      localStorage.setItem('user', JSON.stringify({
        user: response.user,
        accessToken: response.accessToken
      }));

      // Store Stytch session token if present
      if (response.sessionToken) {
        localStorage.setItem('stytch_session_token', response.sessionToken);
      }

      // Clear intermediate session token as it's now consumed/invalid
      dispatch(setIntermediateSessionToken(null));
      localStorage.removeItem('intermediate_session_token');

      console.log('✅ AuthContext: Organization switch complete, navigating to org-scoped URL...');

      // Build organization-scoped URL using centralized helper
      const targetUrl = buildOrgHomeUrl(getOrgSlug(response.organization));

      console.log('🔗 Navigating to:', targetUrl);

      // Reload the page with organization slug in URL to ensure completely clean state
      window.location.href = targetUrl;
    } catch (err: any) {
      console.error('❌ AuthContext: Organization switch failed:', err);
      const errorMessage = err.response?.data?.error || err.message || t('errors.auth.failedToSwitchOrganization', 'Failed to switch organization');
      setLocalError(errorMessage);
      dispatch(setReduxError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('AuthService logout error:', error);
    }

    // Clear organization state
    setCurrentOrganization(null);
    dispatch(clearOrganization());

    // Clear discovery data (CRITICAL for security)
    dispatch({ type: 'organization/clearDiscoveryData' });
    localStorage.removeItem('discovered_organizations');
    localStorage.removeItem('intermediate_session_token');

    // Clear ALL localStorage (CRITICAL for security)
    localStorage.clear();

    // Await the thunk completion to ensure state is cleared BEFORE navigation
    // This prevents race conditions where components might try to fetch data with the old user
    await dispatch(logoutThunk());

    // Force page reload to /login to ensure completely clean state
    window.location.href = '/login';
  };

  // Create a currentUser object that matches the original structure
  const currentUser: AuthResponse | null = userState.user && userState.accessToken
    ? {
      user: userState.user,
      accessToken: userState.accessToken
    }
    : null;

  const value = {
    currentUser,
    currentOrganization,
    isAuthenticated: userState.isAuthenticated,
    isStytchAuth: authService.isStytchAuthenticated(),
    isSwitchingOrganization,
    login,
    loginWithMagicLink,
    authenticateWithStytch,
    createOrganization,
    signup,
    logout,
    switchOrganization,
    loading: userState.loading,
    error: localError || userState.error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 